import { useCheckVerificationsComplete } from 'hooks/useCheckVerificationsComplete';
import { useIsStargazer } from 'hooks/useIsStargazer';
import { useGetRisingStarBonus } from 'services/hooks/risingStarBonus';

import Card from 'components/card/Card';
import withErrorBoundary from 'components/provider/withErrorBoundary';
import Flex from 'components/util/Flex';

import BonusAssetList from './BonusAssetList';
import BonusCalculation from './BonusCalculation';
import BonusHistory from './BonusHistory';
import BonusNotice from './BonusNotice';
import BonusSummary from './BonusSummary';
import { useRisingStarCopy } from './copy';
import TierProgress from './TierProgress';

/**
 * 샛별 신규 애셋 달성 보너스 섹션 (studio#2524).
 *
 * 배치는 별지기 MG 와 같은 대시보드 상단 블록이다 (기획 답변, 10/6). 노출 여부는 등급이 아니라 응답의 `current` 로 정한다
 * — 지급일에 샛별이 아니게 된 분에게도 미지급(CANCELLED) 달을 보여 주기 위해서다 (10/23 기획 답변 A안).
 */
function RisingStarBonusContainer() {
  const COPY = useRisingStarCopy();
  const { data, isError } = useGetRisingStarBonus();
  const { isBankAccountVerificationCompleted, isConvertibleToBubble } = useCheckVerificationsComplete();
  const { isStargazer, isFetched: isGradeFetched } = useIsStargazer();

  // 실패하면 통째로 감춘다(운영 배포 전 404 가 정상 경로). 응답 전에도 그리지 않는다.
  // ponytail: 로딩 자리값 대신 null — 섹션마다 옵셔널 체이닝을 퍼뜨리지 않는다. 깜빡임이 문제 되면 스켈레톤 추가.
  // current 가 null 이면 보여 줄 달이 없다 — 블록을 그릴지는 null 여부로만 정한다 (BE 가이드)
  const current = data?.current;
  // 별지기에게는 적립 내역 카드(StargazerBonusHistory)만 보인다 — 산정 뒤 승급해도 이 블록과 내역이 겹치지 않게 (10/6 기획 답변 D)
  if (isError || !current || !isGradeFetched || isStargazer) return null;

  // 금액 마스킹은 MG·수익 정보 카드와 같은 조건 (디자인 확인 4번 답변 "기존처럼 블러", 10/6)
  const isBlur = !(isBankAccountVerificationCompleted || isConvertibleToBubble);

  // 바깥 Flex 도 여기서 그린다. 부모에 두면 null 일 때(운영 404 등) 빈 flex 아이템이 남아 rowGap 이 한 칸 더 생긴다
  return (
    <Flex isExpanded>
      <Card isExpanded>
        <Card.Header>
          <Card.Title>{COPY.title}</Card.Title>
        </Card.Header>
        <Card.Body>
          <Flex direction="column" rowGap={24}>
            <BonusSummary current={current} isBlur={isBlur} />
            <TierProgress current={current} isBlur={isBlur} />
            <BonusCalculation current={current} isBlur={isBlur} />
            <BonusAssetList assets={current.assets} isBlur={isBlur} />
            <BonusHistory history={data.history ?? []} isBlur={isBlur} />
            <BonusNotice />
          </Flex>
        </Card.Body>
      </Card>
    </Flex>
  );
}

// 렌더 에러도 조용히 감춘다 — BE 문서가 초안(MR !252)이라 응답 형태가 바뀌어 터지면 기본 fallback(GeneralErrorBoundaryUI)이
// 대시보드 상단을 '홈으로 돌아가기' 패널로 덮는다. 부가 섹션 하나 때문에 대시보드를 막을 이유가 없다 (스펙 §2-3).
export default withErrorBoundary(RisingStarBonusContainer, () => null);
